import React from 'react';

import { useAudioRecorder } from '../../../shared/hooks/useAudioRecorder';
import type { ConsultationFieldDefinition } from '../types/Consultation';
import {
  deleteConsultationFieldVoice,
  transcribeConsultationFieldVoice,
  uploadConsultationFieldVoice,
} from '../services/consultationService';
import {
  formatVoiceDuration,
  parseConsultationVoiceSchema,
  parseVoiceValue,
  serializeVoiceValue,
  validateLocalVoiceFile,
} from '../consultationVoiceSchema';
import styles from './ConsultationVoiceFieldInput.module.css';

interface ConsultationVoiceFieldInputProps {
  field: ConsultationFieldDefinition;
  consultationId?: string;
  value: string;
  onChange: (value: string) => void;
  onPendingVoiceChange?: (file: File | null, durationSeconds?: number) => void;
  disabled?: boolean;
}

export const ConsultationVoiceFieldInput: React.FC<ConsultationVoiceFieldInputProps> = ({
  field,
  consultationId,
  value,
  onChange,
  onPendingVoiceChange,
  disabled = false,
}) => {
  const [error, setError] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [pendingFile, setPendingFile] = React.useState<File | null>(null);
  const [pendingDurationSeconds, setPendingDurationSeconds] = React.useState<number | undefined>();

  const schema = React.useMemo(
    () => parseConsultationVoiceSchema(field.schemaJson),
    [field.schemaJson],
  );

  const parsedValue = React.useMemo(() => parseVoiceValue(value), [value]);
  const maxDurationSeconds = schema?.maxDurationSeconds ?? 300;

  React.useEffect(() => {
    onPendingVoiceChange?.(pendingFile, pendingDurationSeconds);
  }, [onPendingVoiceChange, pendingDurationSeconds, pendingFile]);

  const uploadVoiceFile = React.useCallback(async (file: File, durationSeconds?: number) => {
    if (!schema) {
      setError('Voice settings are missing or invalid for this field.');
      return;
    }

    const validationError = validateLocalVoiceFile(schema, file, durationSeconds);
    if (validationError) {
      setError(validationError);
      return;
    }

    if (!consultationId) {
      setPendingFile(file);
      setPendingDurationSeconds(durationSeconds);
      setError('');
      return;
    }

    setBusy(true);
    setError('');
    try {
      const updated = await uploadConsultationFieldVoice(consultationId, field.id, file, durationSeconds);
      onChange(serializeVoiceValue(parseVoiceValue(updated.value)));
      setPendingFile(null);
      setPendingDurationSeconds(undefined);
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : 'Failed to upload voice recording.');
    } finally {
      setBusy(false);
    }
  }, [consultationId, field.id, onChange, schema]);

  const {
    isRecording,
    elapsedSeconds,
    error: recorderError,
    startRecording,
    stopRecording,
  } = useAudioRecorder({
    maxDurationSeconds,
    onRecordingComplete: (file, durationSeconds) => {
      void uploadVoiceFile(file, durationSeconds);
    },
  });

  if (!schema) {
    return (
      <div className={styles.error}>
        Voice settings are missing or invalid for this field.
      </div>
    );
  }

  const handleRemove = async () => {
    if (disabled || busy) {
      return;
    }

    if (!consultationId) {
      setPendingFile(null);
      setPendingDurationSeconds(undefined);
      onChange(serializeVoiceValue({}));
      return;
    }

    setBusy(true);
    setError('');
    try {
      const updated = await deleteConsultationFieldVoice(consultationId, field.id);
      onChange(serializeVoiceValue(parseVoiceValue(updated.value)));
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : 'Failed to remove voice recording.');
    } finally {
      setBusy(false);
    }
  };

  const handleTranscribe = async () => {
    if (!consultationId || disabled || busy || schema.enableTranscription === false) {
      return;
    }

    setBusy(true);
    setError('');
    try {
      const updated = await transcribeConsultationFieldVoice(consultationId, field.id);
      onChange(serializeVoiceValue(parseVoiceValue(updated.value)));
    } catch (transcribeError) {
      setError(transcribeError instanceof Error ? transcribeError.message : 'Failed to transcribe voice recording.');
    } finally {
      setBusy(false);
    }
  };

  const activeAudio = parsedValue.audio;
  const activeTranscription = parsedValue.transcription;
  const showPending = !consultationId && pendingFile;
  const combinedError = error || recorderError;

  return (
    <div>
      <div className={styles.dropZone}>
        <p className={styles.hint}>
          Record a voice note up to {formatVoiceDuration(maxDurationSeconds)}.
          {schema.enableTranscription !== false ? ' Transcription can be generated after recording.' : ''}
        </p>
        <div className={styles.actions}>
          <button
            type="button"
            className={isRecording ? 'btn btn-danger' : 'btn btn-secondary'}
            disabled={disabled || busy}
            onClick={() => {
              if (isRecording) {
                stopRecording();
              } else {
                void startRecording();
              }
            }}
          >
            {isRecording ? 'Stop recording' : activeAudio || showPending ? 'Re-record' : 'Start recording'}
          </button>
          {(activeAudio || showPending) ? (
            <button
              type="button"
              className="btn btn-secondary"
              disabled={disabled || busy || isRecording}
              onClick={() => void handleRemove()}
            >
              Remove
            </button>
          ) : null}
          {schema.enableTranscription !== false && consultationId && activeAudio ? (
            <button
              type="button"
              className="btn btn-secondary"
              disabled={disabled || busy || isRecording || activeTranscription?.status === 'pending'}
              onClick={() => void handleTranscribe()}
            >
              {activeTranscription?.status === 'completed' ? 'Re-transcribe' : 'Transcribe'}
            </button>
          ) : null}
        </div>
        {isRecording ? (
          <p className={styles.recording}>
            Recording… {formatVoiceDuration(elapsedSeconds)}
          </p>
        ) : null}
      </div>

      {!consultationId && pendingFile ? (
        <p className={styles.pending}>Voice recording will upload after the consultation is saved.</p>
      ) : null}

      {activeAudio ? (
        <div className={styles.player}>
          <div className={styles.playerMeta}>
            <div>
              <div className={styles.fileName}>{activeAudio.fileName}</div>
              <div className={styles.details}>
                {formatVoiceDuration(activeAudio.durationSeconds)}
              </div>
            </div>
            <audio controls src={activeAudio.url} preload="metadata" />
          </div>

          {schema.enableTranscription !== false ? (
            <div className={styles.transcription}>
              <span className={styles.transcriptionLabel}>Transcription</span>
              {activeTranscription?.status === 'completed' && activeTranscription.text ? (
                <p className={styles.transcriptionText}>{activeTranscription.text}</p>
              ) : null}
              {activeTranscription?.status === 'pending' ? (
                <p className={styles.details}>Transcription in progress…</p>
              ) : null}
              {activeTranscription?.status === 'failed' ? (
                <p className={styles.error}>{activeTranscription.error || 'Transcription failed.'}</p>
              ) : null}
              {activeTranscription?.status === 'skipped' || !activeTranscription ? (
                <p className={styles.details}>No transcription yet.</p>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {showPending ? (
        <div className={styles.player}>
          <div className={styles.fileName}>{pendingFile.name}</div>
          <div className={styles.details}>
            {formatVoiceDuration(pendingDurationSeconds)} • pending upload
          </div>
        </div>
      ) : null}

      {busy ? <p className={styles.pending}>Saving…</p> : null}
      {combinedError ? <p className={styles.error}>{combinedError}</p> : null}
    </div>
  );
};

export default ConsultationVoiceFieldInput;
